import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { storyData } from '../mock';

export const Story = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-32 bg-gradient-to-br from-gray-50 via-teal-50/30 to-cyan-50/30 relative overflow-hidden">
      {/* Decorative Elements */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 50, repeat: Infinity, ease: 'linear' }}
        className="absolute top-20 right-20 w-64 h-64 border border-teal-200/30 rounded-full"
      />
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
        className="absolute bottom-20 left-20 w-80 h-80 border border-cyan-200/30 rounded-full"
      />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Number */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center gap-4">
            <span className="text-8xl md:text-9xl font-bold text-teal-100">02</span>
            <div>
              <span className="block text-teal-600 font-semibold text-sm uppercase tracking-wider">Chapter Two</span>
              <span className="block text-2xl font-semibold text-gray-900">20+ Years of Excellence</span>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative">
              {/* Background Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-teal-400/30 to-cyan-400/30 rounded-3xl blur-3xl" />
              
              {/* Image Container */}
              <motion.div
                whileHover={{ scale: 1.02, rotate: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="relative rounded-3xl overflow-hidden shadow-2xl"
              >
                <img
                  src={storyData.image}
                  alt={storyData.owner}
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 to-transparent" />
              </motion.div>

              {/* Floating Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
                transition={{ delay: 0.6, type: 'spring', stiffness: 200 }}
                className="absolute -bottom-8 -right-8 bg-white rounded-3xl p-8 shadow-2xl"
              >
                <div className="text-center">
                  <div className="text-5xl font-bold bg-gradient-to-br from-teal-600 to-cyan-600 bg-clip-text text-transparent mb-2">
                    20+
                  </div>
                  <div className="text-gray-600 font-semibold">Years</div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {storyData.title}
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {storyData.description}
            </p>
            <motion.blockquote
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ delay: 0.6 }}
              className="border-l-4 border-teal-500 pl-6 py-4 mb-12"
            >
              <p className="text-lg text-gray-700 italic">{storyData.tagline}</p>
            </motion.blockquote>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-6">
              {storyData.stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="text-center p-4 bg-white rounded-2xl shadow-lg shadow-teal-100/50"
                >
                  <div className="text-sm text-teal-600 font-semibold mb-2 uppercase tracking-wide">
                    {stat.label}
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  {stat.subtitle && (
                    <div className="text-xs text-gray-500">{stat.subtitle}</div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
